"use server"

import { randomUUID } from "node:crypto"
import { sdk } from "@lib/config"
import medusaError from "@lib/util/medusa-error"
import { HttpTypes } from "@medusajs/types"
import { revalidateTag } from "next/cache"
import { after } from "next/server"
import { redirect } from "next/navigation"
import { headers as nextHeaders } from "next/headers"
import { sanitizeIsolatedPreviewCart } from "@lib/util/staged-preview"
import {
  getAuthHeaders,
  getCacheOptions,
  getCacheTag,
  getCartId,
  removeCartId,
  setCartId,
  setOrderAccessToken,
} from "./cookies"
import { getRegion } from "./regions"
import { getLocale } from "./locale-actions"
import { buildGarantiTrustedSessionData, resolveGarantiClientIp } from "@lib/util/garanti-session"
import { retrieveAuthoritativeCheckoutCart } from "./checkout-cart"
import { prepareCheckoutCart } from "./checkout-preparation"
import { mintOrderAccess } from "./orders"
import type { CartPromotionPresentation, CartSnapshot } from "@modules/cart-drawer/types"
import type { MetaTrackingReceipt } from "@vetpets/event-contracts"
import {
  classifyCartWriteState,
  createCommerceInternalHeaders,
  observeCurrentCartAfterMutation,
  observeCartItemAddedAfterMutation,
} from "./commerce-tracking"

/**
 * Retrieves a cart by its ID. If no ID is provided, it will use the cart ID from the cookies.
 * @param cartId - optional - The ID of the cart to retrieve.
 * @returns The cart object if found, or null if not found.
 */
export async function retrieveCart(cartId?: string, fields?: string) {
  const id = cartId || (await getCartId())
  fields ??=
    "*items, *region, *items.product, *items.product.metadata, *items.variant, *items.thumbnail, *items.metadata, items.adjustments.*, +items.total, +items.compare_at_unit_price, +total, *promotions, +shipping_methods.name"

  if (!id) {
    return null
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const next = {
    ...(await getCacheOptions("carts")),
  }

  return await sdk.client
    .fetch<HttpTypes.StoreCartResponse>(`/store/carts/${id}`, {
      method: "GET",
      query: {
        fields,
      },
      headers,
      next,
      cache: "force-cache",
    })
    .then(({ cart }: { cart: HttpTypes.StoreCart }) => sanitizeIsolatedPreviewCart(
      cart,
      process.env.VETPETS_ISOLATED_PREVIEW === "true",
      process.env.VETPETS_TRUSTED_PREVIEW_HOST
    ))
    .catch(() => null)
}

const CART_DRAWER_FIELDS =
  "*items, *region, *items.product, *items.product.metadata, *items.variant, *items.thumbnail, *items.metadata, items.adjustments.*, +items.total, +items.compare_at_unit_price, +total, *promotions, +shipping_methods.name"

type LegacyCartBundlePresentation = {
  bundle_breakdown: unknown[]
  bundle_incentive: {
    bundle_id: string
    option_id: string
    additional_quantity: number
    free_quantity: number
  } | null
  quantity_limits: Record<string, number>
  campaign: { label: string; ends_at: string } | null
}

/**
 * Cached-client compatibility only. Current UI uses native promotion presentation.
 * Keeping this export preserves the previous Server Action ID while the retired
 * bundle read endpoint remains available for already-loaded clients.
 */
export async function retrieveCartBundlePresentation(
  cartId: string
): Promise<LegacyCartBundlePresentation> {
  const headers = { ...(await getAuthHeaders()) }
  const next = { ...(await getCacheOptions("carts")) }
  return sdk.client.fetch<LegacyCartBundlePresentation>(
    `/store/carts/${cartId}/bundle-breakdown`,
    { method: "GET", headers, next, cache: "force-cache" }
  )
}

export async function retrieveCartPromotionPresentation(
  cartId: string
): Promise<CartPromotionPresentation | null> {
  const headers = { ...(await getAuthHeaders()) }
  try {
    return await sdk.client.fetch<CartPromotionPresentation>(
      `/store/carts/${cartId}/promotion-hint`,
      { method: "GET", headers, cache: "no-store" }
    )
  } catch {
    return null
  }
}

export async function retrieveCartSnapshot(): Promise<CartSnapshot> {
  const cartId = await getCartId()
  if (!cartId) {
    return { cart: null, presentation: null, loadError: null }
  }

  try {
    const headers = { ...(await getAuthHeaders()) }
    const next = { ...(await getCacheOptions("carts")) }
    const [cartResponse, presentation] = await Promise.all([
      sdk.client.fetch<HttpTypes.StoreCartResponse>(
        `/store/carts/${cartId}`,
        {
          method: "GET",
          query: { fields: CART_DRAWER_FIELDS },
          headers,
          next,
          cache: "force-cache",
        }
      ),
      retrieveCartPromotionPresentation(cartId),
    ])
    return { cart: cartResponse.cart, presentation, loadError: null }
  } catch {
    return {
      cart: null,
      presentation: null,
      loadError: "Sepet yüklenemedi. Lütfen tekrar deneyin.",
    }
  }
}

const statusFromCartError = (error: any): number =>
  Number(error?.status ?? error?.statusCode ?? error?.response?.status ?? 500)

type StaleCartWriteState = "replaced_by_recovery" | "unavailable"

async function clearStaleCartAfterConflict(
  cartId: string,
  error: unknown
): Promise<StaleCartWriteState | null> {
  if (statusFromCartError(error) !== 409) return null
  try {
    const state = await classifyCartWriteState(cartId)
    if (state !== "replaced_by_recovery" && state !== "unavailable") return null
    await removeCartId()
    const cartTag = await getCacheTag("carts")
    revalidateTag(cartTag)
    const fulfillmentTag = await getCacheTag("fulfillment")
    revalidateTag(fulfillmentTag)
    return state
  } catch {
    return null
  }
}

async function observeCartMutationSafely(cartId: string): Promise<void> {
  try {
    await observeCurrentCartAfterMutation(cartId)
  } catch (error) {
    console.warn("Commerce cart observation failed; reconciliation will repair it", error)
  }
}

export type TrackedCartSnapshot = CartSnapshot & {
  meta_tracking?: MetaTrackingReceipt
}

async function observeCartItemAddedSafely(
  input: Parameters<typeof observeCartItemAddedAfterMutation>[0]
): Promise<MetaTrackingReceipt | undefined> {
  try {
    return await observeCartItemAddedAfterMutation(input)
  } catch {
    console.warn("Commerce AddToCart observation failed")
    return undefined
  }
}

export type SetCartLineQuantityResult =
  | { ok: true; snapshot: CartSnapshot }
  | {
      ok: false
      status: number
      message: string
      reason?: "checkout_replaced_by_recovery" | "checkout_unavailable"
    }

export type SetLogicalLineQuantityResult =
  | { ok: true; snapshot: { cart: HttpTypes.StoreCart; presentation: LegacyCartBundlePresentation; loadError: null } }
  | {
      ok: false
      status: number
      message: string
      reason?: "checkout_replaced_by_recovery" | "checkout_unavailable"
    }

/** Cached-client compatibility only; backend legacy mutation route is retired with 410. */
export async function setLogicalLineQuantity(input: {
  variantId: string
  quantity: number
  expectedLineIds: string[]
}): Promise<SetLogicalLineQuantityResult> {
  const cartId = await getCartId()
  if (!cartId) {
    return { ok: false, status: 404, message: "Sepet bulunamadı." }
  }

  const headers = { ...(await getAuthHeaders()) }
  let response: ({ cart: HttpTypes.StoreCart; bundle_state_hash: string } & LegacyCartBundlePresentation)
  try {
    response = await sdk.client.fetch<
      { cart: HttpTypes.StoreCart; bundle_state_hash: string } & LegacyCartBundlePresentation
    >(`/store/carts/${cartId}/logical-line-quantity`, {
      method: "POST",
      body: {
        variant_id: input.variantId,
        quantity: input.quantity,
        expected_line_ids: input.expectedLineIds,
      },
      headers,
    })
  } catch (error: any) {
    const status = statusFromCartError(error)
    const staleState = await clearStaleCartAfterConflict(cartId, error)
    if (staleState) {
      return {
        ok: false,
        status,
        reason: staleState === "replaced_by_recovery"
          ? "checkout_replaced_by_recovery"
          : "checkout_unavailable",
        message: staleState === "replaced_by_recovery"
          ? "Bu sepet başka bir oturumda kurtarıldı."
          : "Bu sepet artık kullanılamıyor. Lütfen tekrar deneyin.",
      }
    }
    return {
      ok: false,
      status,
      message: String(error?.message ?? "Sepet güncellenemedi."),
    }
  }

  return {
    ok: true,
    snapshot: {
      cart: response.cart,
      presentation: {
        bundle_breakdown: response.bundle_breakdown,
        bundle_incentive: response.bundle_incentive,
        quantity_limits: response.quantity_limits,
        campaign: response.campaign,
      },
      loadError: null,
    },
  }
}

export async function setCartLineQuantity(input: {
  lineId: string
  quantity: number
}): Promise<SetCartLineQuantityResult> {
  const cartId = await getCartId()
  if (!cartId) {
    return { ok: false, status: 404, message: "Sepet bulunamadı." }
  }
  if (!input.lineId || !Number.isInteger(input.quantity) || input.quantity < 0) {
    return { ok: false, status: 400, message: "Geçersiz sepet satırı." }
  }

  const headers = { ...(await getAuthHeaders()) }
  let responseCart: HttpTypes.StoreCart
  try {
    if (input.quantity === 0) {
      const response = await sdk.client.fetch<{
        deleted: boolean
        parent: HttpTypes.StoreCart
      }>(
        `/store/carts/${cartId}/line-items/${input.lineId}`,
        {
          method: "DELETE",
          query: { fields: CART_DRAWER_FIELDS },
          headers,
        }
      )
      responseCart = response.parent
    } else {
      const response = await sdk.client.fetch<{ cart: HttpTypes.StoreCart }>(
        `/store/carts/${cartId}/line-items/${input.lineId}`,
        {
          method: "POST",
          body: { quantity: input.quantity },
          query: { fields: CART_DRAWER_FIELDS },
          headers,
        }
      )
      responseCart = response.cart
    }
  } catch (error: any) {
    const status = statusFromCartError(error)
    const staleState = await clearStaleCartAfterConflict(cartId, error)
    if (staleState) {
      return {
        ok: false,
        status,
        reason: staleState === "replaced_by_recovery"
          ? "checkout_replaced_by_recovery"
          : "checkout_unavailable",
        message: staleState === "replaced_by_recovery"
          ? "Bu sepet başka bir oturumda kurtarıldı."
          : "Bu sepet artık kullanılamıyor. Lütfen tekrar deneyin.",
      }
    }
    return {
      ok: false,
      status,
      message: String(error?.message ?? "Sepet güncellenemedi."),
    }
  }

  after(() => observeCartMutationSafely(cartId))
  const cartTag = await getCacheTag("carts")
  revalidateTag(cartTag)
  const fulfillmentTag = await getCacheTag("fulfillment")
  revalidateTag(fulfillmentTag)

  return {
    ok: true,
    snapshot: {
      cart: responseCart,
      presentation: null,
      loadError: null,
    },
  }
}

export async function getOrSetCart(countryCode: string) {
  const region = await getRegion(countryCode)

  if (!region) {
    throw new Error(`Region not found for country code: ${countryCode}`)
  }

  let cart = await retrieveCart(undefined, "id,region_id")

  const headers = {
    ...(await getAuthHeaders()),
  }

  if (!cart) {
    const locale = await getLocale()
    const cartResp = await sdk.store.cart.create(
      { region_id: region.id, locale: locale || undefined },
      {},
      headers
    )
    cart = cartResp.cart

    await setCartId(cart.id)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  if (cart && cart?.region_id !== region.id) {
    await sdk.store.cart.update(cart.id, { region_id: region.id }, {}, headers)
    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  return cart
}

export async function updateCart(data: HttpTypes.StoreUpdateCart) {
  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("No existing cart found, please create one before updating")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.cart
    .update(cartId, data, {}, headers)
    .then(async ({ cart }: { cart: HttpTypes.StoreCart }) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)

      return cart
    })
    .catch(medusaError)
}

export async function addToCart({
  variantId,
  quantity,
  countryCode,
}: {
  variantId: string
  quantity: number
  countryCode: string
}): Promise<TrackedCartSnapshot> {
  if (!variantId) {
    throw new Error("Missing variant ID when adding to cart")
  }

  const operationId = randomUUID()
  const cart = await getOrSetCart(countryCode)

  if (!cart) {
    throw new Error("Error retrieving or creating cart")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  let targetCart = cart
  const createLine = (cartId: string) => sdk.store.cart.createLineItem(
    cartId,
    {
      variant_id: variantId,
      quantity,
    },
    { fields: CART_DRAWER_FIELDS },
    headers
  )

  let mutationCart: HttpTypes.StoreCart
  try {
    mutationCart = (await createLine(targetCart.id)).cart
  } catch (error) {
    if (!(await clearStaleCartAfterConflict(targetCart.id, error))) throw error
    const replacement = await getOrSetCart(countryCode)
    if (!replacement) throw new Error("Error retrieving or creating replacement cart")
    targetCart = replacement
    mutationCart = (await createLine(targetCart.id)).cart
  }

  const metaTracking = await observeCartItemAddedSafely({
    operationId,
    cartId: targetCart.id,
    kind: "single",
    variantId,
    quantity,
  })

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)

  const fulfillmentCacheTag = await getCacheTag("fulfillment")
  revalidateTag(fulfillmentCacheTag)

  const snapshot: CartSnapshot = {
    cart: mutationCart,
    presentation: null,
    loadError: null,
  }
  return metaTracking ? { ...snapshot, meta_tracking: metaTracking } : snapshot
}

export type AddBundleSelectionInput = {
  countryCode: string
  bundleId: string
  optionId: string
  composition: Array<{ product_id: string; variant_id: string; quantity: number }>
}

/** Cached-client compatibility only; no current consumer imports this action. */
export async function addBundleSelection({
  countryCode,
  bundleId,
  optionId,
  composition,
}: AddBundleSelectionInput): Promise<{
  cart: HttpTypes.StoreCart
  presentation: LegacyCartBundlePresentation
  loadError: null
}> {
  const cart = await getOrSetCart(countryCode)
  if (!cart) throw new Error("Error retrieving or creating cart")
  const headers = { ...(await getAuthHeaders()) }
  const response = await sdk.client.fetch<
    { cart: HttpTypes.StoreCart; bundle_state_hash: string } & LegacyCartBundlePresentation
  >(`/store/carts/${cart.id}/bundle-selection`, {
    method: "POST",
    body: { bundle_id: bundleId, option_id: optionId, composition },
    headers,
  })
  return {
    cart: response.cart,
    presentation: {
      bundle_breakdown: response.bundle_breakdown,
      bundle_incentive: response.bundle_incentive,
      quantity_limits: response.quantity_limits,
      campaign: response.campaign,
    },
    loadError: null,
  }
}

export type AddPurchaseSelectionInput = {
  countryCode: string
  items: Array<{ variant_id: string; quantity: number }>
}

export async function addPurchaseSelection({
  countryCode,
  items,
}: AddPurchaseSelectionInput): Promise<TrackedCartSnapshot> {
  const operationId = randomUUID()
  const cart = await getOrSetCart(countryCode)
  if (!cart) {
    throw new Error("Error retrieving or creating cart")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  let response: { cart: HttpTypes.StoreCart }
  try {
    response = await sdk.client.fetch<{ cart: HttpTypes.StoreCart }>(
      `/store/carts/${cart.id}/purchase-selection`,
      {
        method: "POST",
        body: { items },
        query: { fields: CART_DRAWER_FIELDS },
        headers,
      }
    )
  } catch (error) {
    // Batch add is not blindly retried: the server may have committed before
    // the response became ambiguous. Clear only proven stale recovery carts.
    await clearStaleCartAfterConflict(cart.id, error)
    throw error
  }

  const metaTracking = await observeCartItemAddedSafely({
    operationId,
    cartId: cart.id,
    kind: "selection",
    items,
  })

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)
  const fulfillmentCacheTag = await getCacheTag("fulfillment")
  revalidateTag(fulfillmentCacheTag)

  const snapshot: CartSnapshot = {
    cart: response.cart,
    presentation: null,
    loadError: null,
  }
  return metaTracking ? { ...snapshot, meta_tracking: metaTracking } : snapshot
}

export async function updateLineItem({
  lineId,
  quantity,
}: {
  lineId: string
  quantity: number
}) {
  if (!lineId) {
    throw new Error("Missing lineItem ID when updating line item")
  }

  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("Missing cart ID when updating line item")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .updateLineItem(cartId, lineId, { quantity }, {}, headers)
    .catch(medusaError)

  await observeCartMutationSafely(cartId)

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)

  const fulfillmentCacheTag = await getCacheTag("fulfillment")
  revalidateTag(fulfillmentCacheTag)
}

export async function deleteLineItem(lineId: string) {
  if (!lineId) {
    throw new Error("Missing lineItem ID when deleting line item")
  }

  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("Missing cart ID when deleting line item")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .deleteLineItem(cartId, lineId, {}, headers)
    .catch(medusaError)

  await observeCartMutationSafely(cartId)

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)

  const fulfillmentCacheTag = await getCacheTag("fulfillment")
  revalidateTag(fulfillmentCacheTag)
}

export type CheckoutRecoveryRestorationNotice = {
  removed_lines: string[]
  reduced_quantities: Array<{
    variant_id: string
    requested_quantity: number
    restored_quantity: number
  }>
  promotion_changes: string[]
  shipping_reselection_required: boolean
}

type VetpetsPaymentSessionResponse = {
  payment_collection: HttpTypes.StorePaymentCollection
  restoration_notice: CheckoutRecoveryRestorationNotice | null
}

export async function retrieveCheckoutRecoveryNotice(): Promise<
  CheckoutRecoveryRestorationNotice | null
> {
  const cartId = await getCartId()
  if (!cartId) return null
  const headers = { ...(await getAuthHeaders()) }

  return sdk.client
    .fetch<{ restoration_notice: CheckoutRecoveryRestorationNotice | null }>(
      `/store/carts/${cartId}/vetpets-payment-session`,
      { method: "GET", headers, cache: "no-store" }
    )
    .then(({ restoration_notice }) => restoration_notice)
    .catch(() => null)
}

export async function initiateGarantiPaymentSession() {
  const cartId = await getCartId()
  if (!cartId) {
    throw new Error("No existing cart found when initiating payment")
  }

  const cart = await retrieveAuthoritativeCheckoutCart(cartId)
  const preparedCart = await prepareCheckoutCart(cart)
  const requestHeaders = await nextHeaders()
  const customerIp = resolveGarantiClientIp(
    requestHeaders.get("x-real-ip")?.trim() ?? "",
    process.env.NODE_ENV
  )
  const trustedCart = {
    ...preparedCart,
    shipping_address: {
      ...(preparedCart.shipping_address ?? {}),
      country_code: preparedCart.shipping_address?.country_code || "tr",
    },
  } as HttpTypes.StoreCart
  const trustedSessionData = buildGarantiTrustedSessionData(trustedCart, customerIp)
  const path = `/store/carts/${cartId}/vetpets-payment-session`
  const body = { customer_ip: trustedSessionData.customer_ip }
  const signedHeaders = createCommerceInternalHeaders({
    method: "POST",
    path,
    body: JSON.stringify(body),
  })
  const headers = {
    ...(await getAuthHeaders()),
    ...Object.fromEntries(signedHeaders.entries()),
  }
  await sdk.client.fetch<VetpetsPaymentSessionResponse>(
    path,
    {
      method: "POST",
      body,
      headers,
      cache: "no-store",
    }
  ).catch(medusaError)

  return retrieveAuthoritativeCheckoutCart(cartId)
}

export async function applyPromotions(codes: string[]) {
  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("No existing cart found")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.cart
    .update(cartId, { promo_codes: codes }, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag)
    })
    .catch(medusaError)
}

export async function applyGiftCard(code: string) {
  //   const cartId = getCartId()
  //   if (!cartId) return "No cartId cookie found"
  //   try {
  //     await updateCart(cartId, { gift_cards: [{ code }] }).then(() => {
  //       revalidateTag("cart")
  //     })
  //   } catch (error: any) {
  //     throw error
  //   }
}

export async function removeDiscount(code: string) {
  // const cartId = getCartId()
  // if (!cartId) return "No cartId cookie found"
  // try {
  //   await deleteDiscount(cartId, code)
  //   revalidateTag("cart")
  // } catch (error: any) {
  //   throw error
  // }
}

export async function removeGiftCard(
  codeToRemove: string,
  giftCards: any[]
  // giftCards: GiftCard[]
) {
  //   const cartId = getCartId()
  //   if (!cartId) return "No cartId cookie found"
  //   try {
  //     await updateCart(cartId, {
  //       gift_cards: [...giftCards]
  //         .filter((gc) => gc.code !== codeToRemove)
  //         .map((gc) => ({ code: gc.code })),
  //     }).then(() => {
  //       revalidateTag("cart")
  //     })
  //   } catch (error: any) {
  //     throw error
  //   }
}

export async function submitPromotionForm(
  currentState: unknown,
  formData: FormData
) {
  const code = formData.get("code") as string
  try {
    await applyPromotions([code])
  } catch (e: any) {
    return e.message
  }
}

/**
 * Places an order for a cart. If no cart ID is provided, it will use the cart ID from the cookies.
 * @param cartId - optional - The ID of the cart to place an order for.
 * @returns The cart object if the order was successful, or null if not.
 */
export async function placeOrder(cartId?: string) {
  const id = cartId || (await getCartId())

  if (!id) {
    throw new Error("No existing cart found when placing an order")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const cartRes = await sdk.store.cart
    .complete(id, {}, headers)
    .then(async (cartRes) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)
      return cartRes
    })
    .catch(medusaError)

  if (cartRes?.type === "order") {
    const orderCacheTag = await getCacheTag("orders")
    revalidateTag(orderCacheTag)

    const orderAccessToken = await mintOrderAccess(cartRes.order.id, id)
    await setOrderAccessToken(cartRes.order.id, orderAccessToken)
    await removeCartId()
    redirect(`/order/${cartRes.order.id}/confirmed`)
  }

  return cartRes.cart
}

/**
 * Updates the countrycode param and revalidates the regions cache
 * @param regionId
 * @param countryCode
 */
export async function updateRegion(countryCode: string, currentPath: string) {
  const cartId = await getCartId()
  const region = await getRegion(countryCode)

  if (!region) {
    throw new Error(`Region not found for country code: ${countryCode}`)
  }

  if (cartId) {
    await updateCart({ region_id: region.id })
    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  const regionCacheTag = await getCacheTag("regions")
  revalidateTag(regionCacheTag)

  const productsCacheTag = await getCacheTag("products")
  revalidateTag(productsCacheTag)

  redirect(currentPath || "/")
}

export async function listCartOptions() {
  const cartId = await getCartId()
  const headers = {
    ...(await getAuthHeaders()),
  }
  const next = {
    ...(await getCacheOptions("shippingOptions")),
  }

  return await sdk.client.fetch<{
    shipping_options: HttpTypes.StoreCartShippingOption[]
  }>("/store/shipping-options", {
    query: { cart_id: cartId },
    next,
    headers,
    cache: "force-cache",
  })
}
